import { useState, useEffect, useRef } from 'react';
import { apiGet } from './apiClient';
import type { PnlPoint } from './useTraderProfile';

interface SeriesResponse {
    series_type: string;
    points: PnlPoint[];
}

// module-level so toggling selection never refetches an address we've seen
const seriesCache = new Map<string, PnlPoint[]>();

async function fetchSeries(address: string): Promise<PnlPoint[]> {
    const cached = seriesCache.get(address);
    if (cached) return cached;
    try {
        const res = await apiGet<SeriesResponse>(
            `/api/trader/${address}/series?type=pnl`
        );
        seriesCache.set(address, res.points);
        return res.points;
    } catch {
        // no stats row yet → contributes 0 to the combined curve
        seriesCache.set(address, []);
        return [];
    }
}

function utcMidnight(iso: string): number {
    const d = new Date(iso);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export interface AlignedSeries {
    days: number[];
    cumPnlByAddr: Map<string, number[]>;
    cumPortByAddr: Map<string, (number | null)[]>;
}

// Forward-fill each account's daily cumulative snapshots onto one shared day
// axis so columns are summable and pairwise-comparable.
export function alignSeries(
    perAccount: Map<string, PnlPoint[]>
): AlignedSeries | null {
    const dayset = new Set<number>();
    for (const points of perAccount.values()) {
        for (const p of points) dayset.add(utcMidnight(p.ts));
    }
    const days = [...dayset].sort((a, b) => a - b);
    if (days.length === 0) return null;

    const sorted = new Map<string, PnlPoint[]>();
    for (const [addr, points] of perAccount) {
        sorted.set(
            addr,
            [...points].sort(
                (a, b) => utcMidnight(a.ts) - utcMidnight(b.ts)
            )
        );
    }

    const cumPnlByAddr = new Map<string, number[]>();
    const cumPortByAddr = new Map<string, (number | null)[]>();

    for (const [addr, points] of sorted) {
        const pnlCol: number[] = new Array(days.length);
        const portCol: (number | null)[] = new Array(days.length);
        let i = 0;
        let lastPnl = 0;
        let lastPort: number | null = null;
        for (let d = 0; d < days.length; d++) {
            while (i < points.length && utcMidnight(points[i].ts) <= days[d]) {
                lastPnl = points[i].total_pnl;
                lastPort = points[i].portfolio_value;
                i++;
            }
            pnlCol[d] = lastPnl;
            portCol[d] = lastPort;
        }
        cumPnlByAddr.set(addr, pnlCol);
        cumPortByAddr.set(addr, portCol);
    }

    return { days, cumPnlByAddr, cumPortByAddr };
}

export function combinedFromAligned(aligned: AlignedSeries): PnlPoint[] {
    const { days, cumPnlByAddr, cumPortByAddr } = aligned;
    const out: PnlPoint[] = new Array(days.length);
    for (let d = 0; d < days.length; d++) {
        let sumPnl = 0;
        let sumPort = 0;
        let anyPort = false;
        for (const col of cumPnlByAddr.values()) sumPnl += col[d];
        for (const col of cumPortByAddr.values()) {
            const v = col[d];
            if (v != null) {
                sumPort += v;
                anyPort = true;
            }
        }
        out[d] = {
            ts: new Date(days[d]).toISOString(),
            total_pnl: Number(sumPnl.toFixed(2)),
            portfolio_value: anyPort ? Number(sumPort.toFixed(2)) : null,
        };
    }
    return out;
}

export function maxDrawdown(series: PnlPoint[]): {
    abs: number;
    pct: number | null;
} {
    let peak = -Infinity;
    let maxDd = 0;
    for (const p of series) {
        if (p.total_pnl > peak) peak = p.total_pnl;
        const dd = peak - p.total_pnl;
        if (dd > maxDd) maxDd = dd;
    }
    // pct only meaningful once the peak is above water
    return { abs: maxDd, pct: peak > 0 ? maxDd / peak : null };
}

function pearson(a: number[], b: number[]): number | null {
    const n = Math.min(a.length, b.length);
    if (n < 2) return null;
    let sa = 0;
    let sb = 0;
    for (let i = 0; i < n; i++) {
        sa += a[i];
        sb += b[i];
    }
    const ma = sa / n;
    const mb = sb / n;
    let cov = 0;
    let va = 0;
    let vb = 0;
    for (let i = 0; i < n; i++) {
        const da = a[i] - ma;
        const db = b[i] - mb;
        cov += da * db;
        va += da * da;
        vb += db * db;
    }
    if (va === 0 || vb === 0) return null;
    return cov / Math.sqrt(va * vb);
}

// Mean pairwise correlation of daily PnL *changes* — the diversification signal.
export function avgPairwiseCorrelation(
    aligned: AlignedSeries
): number | null {
    const deltas: number[][] = [];
    for (const col of aligned.cumPnlByAddr.values()) {
        const d: number[] = [];
        for (let i = 1; i < col.length; i++) d.push(col[i] - col[i - 1]);
        deltas.push(d);
    }
    if (deltas.length < 2) return null;
    let sum = 0;
    let count = 0;
    for (let i = 0; i < deltas.length; i++) {
        for (let j = i + 1; j < deltas.length; j++) {
            const c = pearson(deltas[i], deltas[j]);
            if (c != null) {
                sum += c;
                count++;
            }
        }
    }
    return count > 0 ? sum / count : null;
}

export function useAggregatePnlSeries(addresses: string[]): {
    aligned: AlignedSeries | null;
    combined: PnlPoint[];
    loading: boolean;
    error: string | null;
} {
    const [aligned, setAligned] = useState<AlignedSeries | null>(null);
    const [combined, setCombined] = useState<PnlPoint[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const callIdRef = useRef(0);

    const key = [...addresses].sort().join(',');

    useEffect(() => {
        if (!key) {
            setAligned(null);
            setCombined([]);
            setError(null);
            return;
        }
        const addrs = key.split(',').filter(Boolean);
        const callId = ++callIdRef.current;
        setLoading(true);
        setError(null);

        Promise.all(addrs.map(a => fetchSeries(a).then(pts => [a, pts] as const)))
            .then(entries => {
                if (callId !== callIdRef.current) return;
                const perAccount = new Map(entries);
                const al = alignSeries(perAccount);
                setAligned(al);
                setCombined(al ? combinedFromAligned(al) : []);
            })
            .catch(err => {
                if (callId !== callIdRef.current) return;
                setError(err instanceof Error ? err.message : String(err));
                setAligned(null);
                setCombined([]);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });
    }, [key]);

    return { aligned, combined, loading, error };
}